import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import './CreativeSpace.css';

import heroImg from '../assets/YOCILAB Webiste photos/YOCILAB Creative Space/Creative space.jpg';
import spaceImg1 from '../assets/YOCILAB Webiste photos/YOCILAB Creative Space/DSC_3120.jpg';
import spaceImg2 from '../assets/YOCILAB Webiste photos/YOCILAB Creative Space/_DSC1426.jpg';
import spaceImg3 from '../assets/YOCILAB Webiste photos/YOCILAB Creative Space/DSC_4594.jpg';

const features = [
  {
    title: 'Co-working & Collaboration Desks',
    desc: 'Flexible hot desks, ergonomic dedicated pods, and communal tables powered by enterprise fiber wifi and solar backup — designed for focused deep work and spontaneous collaboration.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/>
      </svg>
    ),
  },
  {
    title: 'Creator & Podcast Booth',
    desc: 'Acoustic-treated studio with professional podcast microphones, lighting rigs, and screen recording setups for content creators, educators, and media entrepreneurs.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"/><path d="M19 10v2a7 7 0 0 1-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/>
      </svg>
    ),
  },
  {
    title: 'Smart Meeting Rooms',
    desc: 'Private rooms equipped with smart displays, video-conferencing systems, and whiteboards for team sprints, investor pitches, and client presentations.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
      </svg>
    ),
  },
  {
    title: 'Executive Boardroom & Event Hall',
    desc: 'A fully equipped boardroom and flexible event hall for hackathons, product launches, panel discussions, community gatherings, and stakeholder roundtables.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/>
      </svg>
    ),
  },
  {
    title: 'Café & Networking Lounge',
    desc: 'An open lounge designed for serendipitous conversations, fresh coffee, casual mentorship, and the kind of knowledge sharing that only happens when builders are in the same room.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 8h1a4 4 0 0 1 0 8h-1"/><path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"/><line x1="6" y1="1" x2="6" y2="4"/><line x1="10" y1="1" x2="10" y2="4"/><line x1="14" y1="1" x2="14" y2="4"/>
      </svg>
    ),
  },
  {
    title: 'Reliable Power & High-Speed Internet',
    desc: 'Solar backup with generator support ensures zero interruption during power cuts. Gigabit fiber internet keeps your workflows, video calls, and builds running at full speed.',
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
      </svg>
    ),
  },
];

const passes = [
  {
    tier: 'Day Builder',
    price: '$5 / day',
    desc: 'Perfect for freelancers and builders needing a high-performance workspace for a focused sprint.',
    features: ['Open co-working desk access', 'High-speed fiber internet', 'Café & lounge access', 'Community event invitations'],
    popular: false,
  },
  {
    tier: 'Resident Innovator',
    price: '$60 / month',
    desc: 'For dedicated founders, developers, and creators scaling active ventures with full hub access.',
    features: ['Dedicated desk & locker storage', 'Unlimited 24/7 access', '5 hrs meeting room credits/mo', 'Creator & podcast booth access', '1-on-1 mentor matching', 'YOCILAB Academy discounts'],
    popular: true,
  },
  {
    tier: 'Startup Team Studio',
    price: '$200 / month',
    desc: 'Private cluster for early-stage teams building products and closing seed rounds.',
    features: ['Private 4–6 desk team suite', 'Unlimited meeting room access', 'Venture incubator fast-track', 'Investor pitch day showcase', 'Priority demo booth at Youth Summit'],
    popular: false,
  },
];

const CreativeSpace = () => (
  <div className="cs-page">
    <SEO
      title="Creative Space | YOCILAB"
      description="YOCILAB Creative Space is a vibrant physical co-working, creation, and collaboration hub for young innovators, builders, and entrepreneurs in Cameroon."
    />

    {/* HERO */}
    <section className="cs-hero">
      <div className="cs-hero__bg">
        <img src={heroImg} alt="YOCILAB Creative Space" className="cs-hero__img" />
        <div className="cs-hero__overlay" />
      </div>
      <div className="container">
        <div className="cs-hero__badge">
          <span className="cs-hero__badge-dot" />
          PHYSICAL INNOVATION LAUNCHPAD
        </div>
        <h1 className="cs-hero__title">
          A Space Built for<br />
          <em>Bold African Ideas</em>
        </h1>
        <p className="cs-hero__subtitle">
          Co-working, creation, and collaboration for young innovators, technologists, and entrepreneurs to build, connect, and thrive.
        </p>
      </div>
    </section>

    {/* INTRO + PHOTOS */}
    <section className="cs-intro-section">
      <div className="container">
        <div className="cs-intro-grid">
          <div className="cs-intro-text">
            <span className="section-pill">WHY THE CREATIVE SPACE</span>
            <h2>Where builders build — and connections happen.</h2>
            <p>
              Great ideas need more than a laptop. They need a community of peers who challenge you, mentors who open doors, and infrastructure that never slows you down. YOCILAB Creative Space gives young Africans exactly that — a physical home for ambition.
            </p>
            <p>
              Whether you're writing code at midnight, recording your first podcast episode, hosting an investor demo, or leading a community workshop, the Creative Space has a place for you.
            </p>
            <Link to="/contact" className="cs-link">Request a free day pass →</Link>
          </div>
          <div className="cs-intro-photos">
            <img src={spaceImg1} alt="Creative Space interior" className="cs-photo cs-photo--main" loading="lazy" />
            <div className="cs-photo-duo">
              <img src={spaceImg2} alt="Co-working area" className="cs-photo" loading="lazy" />
              <img src={spaceImg3} alt="Workshop space" className="cs-photo" loading="lazy" />
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* FEATURES */}
    <section className="cs-features-section">
      <div className="container">
        <div className="cs-section-header">
          <span className="section-pill">FACILITIES</span>
          <h2>Everything you need to build without limits.</h2>
          <p>Designed from the ground up to remove friction so you can focus on what matters.</p>
        </div>
        <div className="cs-features-grid">
          {features.map((f, idx) => (
            <div key={idx} className="cs-feature-card">
              <div className="cs-feature-card__icon">{f.icon}</div>
              <h3 className="cs-feature-card__title">{f.title}</h3>
              <p className="cs-feature-card__desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* PRICING */}
    <section className="cs-pricing-section" id="passes">
      <div className="container">
        <div className="cs-section-header">
          <span className="section-pill">WORKSPACE PASSES</span>
          <h2>Choose your builder mode.</h2>
          <p>Flexible access designed for freelancers, founders, and startup teams.</p>
        </div>
        <div className="cs-pricing-grid">
          {passes.map((p) => (
            <div key={p.tier} className={`cs-pass-card ${p.popular ? 'cs-pass-card--popular' : ''}`}>
              {p.popular && <span className="cs-pass-card__badge">Most Popular</span>}
              <h3 className="cs-pass-card__tier">{p.tier}</h3>
              <p className="cs-pass-card__price">{p.price}</p>
              <p className="cs-pass-card__desc">{p.desc}</p>
              <ul className="cs-pass-card__features">
                {p.features.map((feat, i) => (
                  <li key={i} className="cs-pass-card__feat">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                    {feat}
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="cs-pass-card__cta">Get Started</Link>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="cs-cta-section">
      <div className="container">
        <div className="cs-cta-block">
          <h2>Come build with us.</h2>
          <p>YOCILAB Creative Space is open to entrepreneurs, freelancers, university students, community builders, and anyone who believes that Africa's future is being built right now.</p>
          <div className="cs-cta-actions">
            <Link to="/contact" className="btn btn-accent">Book Your Space</Link>
            <Link to="/programs/yocilab-hub" className="btn btn-outline-white">Explore the Full Hub</Link>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default CreativeSpace;
